import CustomInput from "../../atoms/customInput/CustomInput";
import SpecificButton from "../../atoms/specificButton/SpecificButton";
import { LanguageContext } from "../../utils/context/languageContext";
import { LanguageKeys } from "../../utils/languageKeys";
import { convertToCentimeters, handleErrorMessage } from "../../utils/utils";
import { MenuItem, Select } from "@mui/material";
import { ArrowRight } from "lucide-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useConfig } from "../../utils/context/configContext";

interface HeightStepProps {
	onComplete: (value: any) => void;
	initialValue?: any;
	onStepComplete?: (value: any) => Promise<void> | void;
}

const HeightStep = ({ onComplete, initialValue, onStepComplete }: HeightStepProps) => {
	const [localHeight, setLocalHeight] = useState({ cm: initialValue ? String(initialValue) : "", ft: "", inch: "" });
	const [measurementUnit, setMeasurementUnit] = useState("cm");
	const [message, setMessage] = useState<string | undefined>(undefined);
	const { translate, preferredLanguage } = useContext(LanguageContext) || {};
	const config = useConfig();
	const input = config?.style?.input;
	const priority = input?.priority;

	const inputBg = priority?.inputBackgroundColor || input?.inputBackgroundColor || "#F9FAFC";
	const inputText = priority?.inputTextColor || input?.inputTextColor || "#000";

	const handleSetHeight = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setMessage(undefined);
			if (measurementUnit === "cm") {
				setLocalHeight((prev) => ({ ...prev, cm: event.target.value, ft: "", inch: "" }));
			} else if (event.target.id === "ft") {
				setLocalHeight((prev) => ({ ...prev, ft: event.target.value, cm: "" }));
			} else {
				setLocalHeight((prev) => ({ ...prev, inch: event.target.value, cm: "" }));
			}
		},
		[measurementUnit],
	);

	const handleMeasurementUnit = useCallback((event: any) => {
		const val = event.target.value;
		setMeasurementUnit(val);
		setLocalHeight({ cm: "", ft: "", inch: "" });
		setMessage(undefined);
	}, []);

	const validateHeight = useCallback(
		(height: { cm: string; ft: string; inch: string }) => {
			if (measurementUnit === "cm") {
				return !(+height.cm < 152.4 || +height.cm > 213.36);
			}
			return !(convertToCentimeters(+height.ft, +height.inch) < 152.4 || convertToCentimeters(+height.ft, +height.inch) > 213.36);
		},
		[measurementUnit],
	);

	const checkMeasurement = useCallback(async () => {
		try {
			if (!validateHeight(localHeight)) {
				setMessage(translate?.(LanguageKeys.heightError));
				return;
			}
			const height = { cm: +localHeight.cm, ft: +localHeight.ft, inch: +localHeight.inch };
			await onStepComplete?.(height);
			onComplete?.(height);
		} catch (error) {
			setMessage(handleErrorMessage(error));
		}
	}, [localHeight, validateHeight, onStepComplete]);

	const isButtonDisabled = useMemo(() => (!localHeight.cm && !localHeight.ft && !localHeight.inch) || !!message, [localHeight, message]);

	useEffect(() => {
		if (localHeight.cm === "" && localHeight.ft !== "") {
			setMeasurementUnit("ft");
		}
	}, [localHeight]);

	const renderHeightInput = useCallback(() => {
		if (measurementUnit === "cm") {
			return (
				<div className={`w-full`}>
					<CustomInput
						required
						type="number"
						id="cm"
						data-testid="onboarding-height-input"
						placeholder={translate?.(LanguageKeys.height)}
						className="!pr-[10px]"
						inputMode="numeric"
						value={localHeight.cm}
						onChange={handleSetHeight}
					/>
				</div>
			);
		} else {
			return (
				<div className="flex gap-[.5rem]">
					<div>
						<CustomInput
							required
							type="number"
							id="ft"
							className="!pr-[10px]"
							placeholder={translate?.(LanguageKeys.heightInFt)}
							value={localHeight.ft}
							onChange={handleSetHeight}
						/>
					</div>
					<div>
						<CustomInput
							required
							type="number"
							id="inch"
							className="!pr-[10px]"
							placeholder={translate?.(LanguageKeys.heightInInch)}
							value={localHeight.inch}
							onChange={handleSetHeight}
						/>
					</div>
				</div>
			);
		}
	}, [localHeight, preferredLanguage]);

	return (
		<div className="h-full  w-full">
			<div className="mt-[3.5rem] mb-[.75rem]">
				<div className="w-full flex gap-[.5rem]">
					{renderHeightInput()}
					<Select
						className="bg-btn outline-none h-[40px] [&_svg]:text-base !shadow-none !outline-none !text-base"
						value={measurementUnit}
						onChange={handleMeasurementUnit}
						style={{
							background: inputBg,
							color: inputText,
							fontSize: input?.inputFontSize || "16px",
							fontWeight: input?.inputFontWeight || "400",
							border: `1px solid ${input?.inputBorderColor || "transparent"}`,
							fontFamily: config?.style?.base?.baseFontFamily || "Inter, sans-serif",
							borderRadius: input?.inputBorderRadius || "4px",
						}}
					>
						<MenuItem value="cm">{translate?.(LanguageKeys.cmMeasurementUnit)}</MenuItem>
						<MenuItem value="ft">{translate?.(LanguageKeys.ftMeasurementUnit)}</MenuItem>
					</Select>
					<style>
						{`
            .MuiOutlinedInput-notchedOutline {
              border: 1px solid ${input?.inputBorderColor || "transparent"} !important;
              outline: none;
            }
              .MuiSelect-select{
               outline: none;
              }
               .MuiSvgIcon-root{
               color: ${inputText} !important;
               }
          `}
					</style>
				</div>
				<div
					className="mt-2"
					style={{
						color: input?.inputErrorColor || "#000",
						fontSize: input?.inputErrorFontSize || "16px",
					}}
				>
					{message}
				</div>
			</div>
			<div className="flex justify-end">
				<SpecificButton disabled={isButtonDisabled} data-testid="onboarding-next-btn" buttonFunc={checkMeasurement} buttonText={translate?.(LanguageKeys.next)} postfixIcon={<ArrowRight size={14} />} />
			</div>
		</div>
	);
};

export default HeightStep;
